import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSetCard3DSStore } from '@lib/store';
import type { GetAllCardsResponse } from '@shared/api';
import { Button, Error, Input, Modal, Select } from '@shared/widgets';
import { codes } from '@assets/data/codes.json';

interface IForm {
  countryCode: string;
  phone: string;
}
const DEFAULT_FORM: IForm = { countryCode: '', phone: '' };

interface IUpdate3dSecureNumberModalProps {
  card: GetAllCardsResponse;
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
}

export const Update3dSecureNumberModal = (props: IUpdate3dSecureNumberModalProps) => {
  const { loading, error, success, set, resetError, clear } = useSetCard3DSStore();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
  } = useForm<IForm>({ defaultValues: DEFAULT_FORM });

  useEffect(() => {
    if (loading) return;
    if (error) {
      setError('root', { message: 'Something went wrong. Please try again or contact customer service.' });
      return;
    }
    if (success) {
      clear();
      reset(DEFAULT_FORM);
      props.onClose();
      props.onConfirm?.();
    }
  }, [loading, error, success]);

  const closeHandler = () => {
    reset(DEFAULT_FORM);
    resetError();
    props.onClose();
  };
  const update3dSecureNumberHandler = (data: IForm) => set(data, props.card.iclCardId);

  return (
    <Modal title='Update 3D Secure Number' isOpen={props.isOpen} onClose={closeHandler}>
      <form onSubmit={handleSubmit(update3dSecureNumberHandler)}>
        <div className='mb-4 text-left'>
          <Controller
            name='countryCode'
            control={control}
            rules={{ required: 'Country code is required' }}
            render={({ field, fieldState: { error } }) => (
              <div className='flex flex-col gap-2'>
                <Select {...field} label='Code' name='countryCode'>
                  <option value='' disabled>
                    Select country code
                  </option>
                  {codes.map((code, idx) => (
                    <option key={idx} value={code[0]}>
                      +{code[0]} ({code[1]})
                    </option>
                  ))}
                </Select>
                <Error text={error?.message} />
              </div>
            )}
          />
        </div>
        <Controller
          name='phone'
          control={control}
          rules={{ required: 'Phone is required', minLength: { value: 7, message: 'The phone number format is incorrect' } }}
          render={({ field, fieldState: { error } }) => (
            <div className='flex flex-col gap-2'>
              <Input
                {...field}
                label='Phone number'
                inputClassName='font-inherit mt-1 block h-[50px] w-full rounded-lg border border-[#4c4c4c1a] bg-[#fafafa] px-4 py-3 text-[#0f0f11]'
                name='phone'
              />
              <Error text={error?.message} />
            </div>
          )}
        />
        <Error className='mt-6' text={errors.root?.message} />
        <div className='mt-8 flex gap-4'>
          <Button fullWidth color='outlined' onClick={closeHandler}>
            Cancel
          </Button>
          <Button fullWidth type='submit' isLoading={loading}>
            Update
          </Button>
        </div>
      </form>
    </Modal>
  );
};
